import type React from "react";

export type PgnRichTextTag = "strong" | "em" | "u" | "s" | "br" | "span" | "a";

export type PgnRichTextStyle = Pick<
  React.CSSProperties,
  "color" | "fontSize"
>;

export type PgnRichTextAttrs = {
  href?: string;
  style?: PgnRichTextStyle;
};

export type PgnRichTextNode =
  | { kind: "text"; text: string }
  | {
      kind: "element";
      tag: PgnRichTextTag;
      attrs: PgnRichTextAttrs;
      children: PgnRichTextNode[];
    };

type StackFrame = Extract<PgnRichTextNode, { kind: "element" }>;

const TAG_ALIASES: Record<string, PgnRichTextTag | undefined> = {
  a: "a",
  b: "strong",
  br: "br",
  em: "em",
  font: "span",
  i: "em",
  s: "s",
  span: "span",
  strong: "strong",
  u: "u",
};

const FONT_SIZE_BY_LEGACY_SIZE: Record<string, string | undefined> = {
  "1": "0.75em",
  "2": "0.875em",
  "3": "1em",
  "4": "1.125em",
  "5": "1.5em",
  "6": "2em",
  "7": "3em",
};

const ATTRIBUTE_PATTERN = /([a-zA-Z][\w:-]*)\s*=\s*(['"])(.*?)\2/g;
const TAG_PATTERN = /<[^>]*>/g;
const URL_PATTERN = /\b(?:https?:\/\/[^\s<]+|www\.[^\s<]+)/gi;
const TRAILING_PUNCTUATION_PATTERN = /[),.;:!?]+$/;
const SAFE_COLOR_PATTERN =
  /^(#[\da-f]{3,8}|[a-z]+|rgb\(\s*\d{1,3}\s*,\s*\d{1,3}\s*,\s*\d{1,3}\s*\))$/i;
const SAFE_FONT_SIZE_PATTERN =
  /^(\d+(?:\.\d+)?(?:px|em|rem|%)|small|medium|large|x-small|x-large)$/i;

function appendText(nodes: PgnRichTextNode[], text: string) {
  if (text.length === 0) {
    return;
  }

  const lastNode = nodes[nodes.length - 1];
  if (lastNode?.kind === "text") {
    lastNode.text += text;
    return;
  }

  nodes.push({ kind: "text", text });
}

function parseAttributes(rawTag: string): Record<string, string> {
  const attrs: Record<string, string> = {};
  for (const match of rawTag.matchAll(ATTRIBUTE_PATTERN)) {
    const name = match[1];
    const value = match[3];
    if (name !== undefined && value !== undefined) {
      attrs[name.toLowerCase()] = value;
    }
  }
  return attrs;
}

function normalizeHref(rawHref: string | undefined): string | undefined {
  if (!rawHref) {
    return undefined;
  }

  const trimmedHref = rawHref.trim();
  if (trimmedHref.startsWith("/")) {
    return trimmedHref.startsWith("//") ? undefined : trimmedHref;
  }

  try {
    const url = new URL(trimmedHref);
    return url.protocol === "http:" || url.protocol === "https:"
      ? url.href
      : undefined;
  } catch {
    return undefined;
  }
}

function normalizeAutoHref(rawUrl: string): string | undefined {
  const candidate = rawUrl.toLowerCase().startsWith("www.")
    ? `https://${rawUrl}`
    : rawUrl;

  return normalizeHref(candidate);
}

function normalizeStyleValue(name: string, value: string): PgnRichTextStyle {
  const trimmedValue = value.trim();
  if (name === "color" && SAFE_COLOR_PATTERN.test(trimmedValue)) {
    return { color: trimmedValue };
  }
  if (name === "font-size" && SAFE_FONT_SIZE_PATTERN.test(trimmedValue)) {
    return { fontSize: trimmedValue };
  }
  return {};
}

function parseInlineStyle(style: string | undefined): PgnRichTextStyle | undefined {
  if (!style) {
    return undefined;
  }

  const normalizedStyle: PgnRichTextStyle = {};
  for (const declaration of style.split(";")) {
    const [name, ...valueParts] = declaration.split(":");
    if (!name || valueParts.length === 0) {
      continue;
    }

    Object.assign(
      normalizedStyle,
      normalizeStyleValue(name.trim().toLowerCase(), valueParts.join(":")),
    );
  }

  return Object.keys(normalizedStyle).length > 0 ? normalizedStyle : undefined;
}

function normalizeAttrs(
  tagName: string,
  tag: PgnRichTextTag,
  attrs: Record<string, string>,
): PgnRichTextAttrs {
  const normalizedAttrs: PgnRichTextAttrs = {};

  if (tag === "a") {
    normalizedAttrs.href = normalizeHref(attrs.href);
  }

  const style = parseInlineStyle(attrs.style);
  if (style) {
    normalizedAttrs.style = style;
  }

  if (tagName === "font") {
    const fontStyle: PgnRichTextStyle = {};
    if (attrs.color && SAFE_COLOR_PATTERN.test(attrs.color.trim())) {
      fontStyle.color = attrs.color.trim();
    }
    if (attrs.size && FONT_SIZE_BY_LEGACY_SIZE[attrs.size.trim()]) {
      fontStyle.fontSize = FONT_SIZE_BY_LEGACY_SIZE[attrs.size.trim()];
    }
    if (Object.keys(fontStyle).length > 0) {
      normalizedAttrs.style = { ...normalizedAttrs.style, ...fontStyle };
    }
  }

  return normalizedAttrs;
}

function parseTagToken(token: string):
  | { kind: "unsupported" }
  | { kind: "closing"; tag: PgnRichTextTag }
  | { kind: "opening"; tag: PgnRichTextTag; attrs: PgnRichTextAttrs } {
  const match = /^<\s*(\/)?\s*([a-zA-Z][\w-]*)([\s\S]*?)\/?\s*>$/.exec(
    token,
  );
  if (!match) {
    return { kind: "unsupported" };
  }

  const rawTagName = match[2];
  if (!rawTagName) {
    return { kind: "unsupported" };
  }

  const tagName = rawTagName.toLowerCase();
  const tag = TAG_ALIASES[tagName];
  if (!tag) {
    return { kind: "unsupported" };
  }

  if (match[1]) {
    return { kind: "closing", tag };
  }

  return {
    kind: "opening",
    tag,
    attrs: normalizeAttrs(tagName, tag, parseAttributes(match[3] ?? "")),
  };
}

function currentChildren(
  roots: PgnRichTextNode[],
  stack: StackFrame[],
): PgnRichTextNode[] {
  return stack[stack.length - 1]?.children ?? roots;
}

function findLastStackIndex(stack: StackFrame[], tag: PgnRichTextTag): number {
  for (let index = stack.length - 1; index >= 0; index -= 1) {
    if (stack[index]?.tag === tag) {
      return index;
    }
  }
  return -1;
}

function splitTrailingPunctuation(urlText: string) {
  const punctuation =
    TRAILING_PUNCTUATION_PATTERN.exec(urlText)?.[0] ?? "";
  return {
    punctuation,
    url: punctuation ? urlText.slice(0, -punctuation.length) : urlText,
  };
}

function linkifyText(text: string): PgnRichTextNode[] {
  const nodes: PgnRichTextNode[] = [];
  let lastIndex = 0;

  for (const match of text.matchAll(URL_PATTERN)) {
    const rawMatch = match[0];
    const index = match.index ?? 0;
    const { punctuation, url } = splitTrailingPunctuation(rawMatch);
    const href = normalizeAutoHref(url);
    if (!href) {
      continue;
    }

    appendText(nodes, text.slice(lastIndex, index));
    nodes.push({
      kind: "element",
      tag: "a",
      attrs: { href },
      children: [{ kind: "text", text: url }],
    });
    appendText(nodes, punctuation);
    lastIndex = index + rawMatch.length;
  }

  appendText(nodes, text.slice(lastIndex));
  return nodes;
}

function linkifyNodes(nodes: PgnRichTextNode[]): PgnRichTextNode[] {
  return nodes.flatMap((node): PgnRichTextNode[] => {
    if (node.kind === "text") {
      return linkifyText(node.text);
    }
    if (node.tag === "a") {
      return [node];
    }
    return [{ ...node, children: linkifyNodes(node.children) }];
  });
}

export function parsePgnRichText(text: string | null | undefined): PgnRichTextNode[] {
  if (!text) {
    return [];
  }

  const roots: PgnRichTextNode[] = [];
  const stack: StackFrame[] = [];
  let lastIndex = 0;

  for (const match of text.matchAll(TAG_PATTERN)) {
    const token = match[0];
    const index = match.index ?? 0;
    appendText(currentChildren(roots, stack), text.slice(lastIndex, index));

    const parsedTag = parseTagToken(token);
    if (parsedTag.kind === "unsupported") {
      appendText(currentChildren(roots, stack), token);
    } else if (parsedTag.kind === "closing") {
      const matchingIndex = findLastStackIndex(stack, parsedTag.tag);
      if (matchingIndex === -1) {
        appendText(currentChildren(roots, stack), token);
      } else {
        stack.splice(matchingIndex);
      }
    } else if (parsedTag.tag === "br") {
      currentChildren(roots, stack).push({
        kind: "element",
        tag: "br",
        attrs: {},
        children: [],
      });
    } else {
      const node: StackFrame = {
        kind: "element",
        tag: parsedTag.tag,
        attrs: parsedTag.attrs,
        children: [],
      };
      currentChildren(roots, stack).push(node);
      stack.push(node);
    }

    lastIndex = index + token.length;
  }

  appendText(currentChildren(roots, stack), text.slice(lastIndex));
  return linkifyNodes(roots);
}

export function getPgnRichTextVisibleText(
  text: string | null | undefined,
): string {
  const collectText = (nodes: PgnRichTextNode[]): string =>
    nodes
      .map((node) =>
        node.kind === "text" ? node.text : collectText(node.children),
      )
      .join("");

  return collectText(parsePgnRichText(text));
}
